import * as React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { AppBar, Box, Drawer, IconButton, Toolbar } from '@mui/material';
import Sidebar from './components/Sidebar';
import { Menu as MenuIcon } from '@mui/icons-material';
import ProfileMenu from './components/ProfileMenu';
import AuthContext from '../../../features/auth/service/authContext';

type LayoutProps = {
  children: React.ReactChild;

  renderLeftToolbar?: () => React.ReactElement;
  renderRightToolbar?: () => React.ReactElement;
};

const drawerWidth = 240;

const Layout: React.FC<LayoutProps> = ({
  children,
  renderLeftToolbar = () => <></>,
  renderRightToolbar = () => <></>,
}) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { logoutUser } = React.useContext(AuthContext);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          borderBottom: 1,
          borderColor: 'grey.300',
          bgcolor: 'white',
          color: 'text.primary',
        }}
        elevation={0}
      >
        <Toolbar variant="dense">
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>{renderLeftToolbar()}</Box>
          <Box>
            {renderRightToolbar()}
            <ProfileMenu onLogout={logoutUser!} />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          <Sidebar />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          <Sidebar />
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar variant="dense" />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
